import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { products } from '../data/mockData';
import { FiSearch, FiAlertCircle, FiCheckCircle, FiHelpCircle } from 'react-icons/fi';

const ProductSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('ALL');

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.brand.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          product.product.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'ALL' || product.status === filter;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'RECALLED': return <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs font-bold flex items-center gap-1"><FiAlertCircle /> RECALLED</span>;
      case 'SAFE': return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-bold flex items-center gap-1"><FiCheckCircle /> PASS</span>;
      case 'WARNING': return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-bold flex items-center gap-1"><FiHelpCircle /> WARNING</span>;
      default: return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Product Safety Database | Recall Moringa</title>
      </Helmet>
      
      <div className="bg-white min-h-screen">
        {/* Header */}
        <div className="bg-primary-dark text-white py-12">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold mb-4">Product Safety Database</h1>
            <p className="text-blue-200 max-w-2xl mx-auto mb-8">
              Search our database of tested moringa products to check for recalls, safety violations, and quality assurance ratings.
            </p>
            
            <div className="max-w-xl mx-auto relative">
              <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl" />
              <input 
                type="text" 
                placeholder="Search by brand or product name..." 
                className="w-full pl-12 pr-4 py-4 rounded-lg text-gray-900 shadow-lg focus:outline-none focus:ring-4 focus:ring-primary-light/50"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex gap-4 mb-6 overflow-x-auto pb-2">
            {['ALL', 'SAFE', 'WARNING', 'RECALLED'].map(f => (
              <button 
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                  filter === f ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f === 'ALL' ? 'All Products' : f}
              </button>
            ))}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="p-4 text-sm font-semibold text-gray-600">Brand / Product</th>
                    <th className="p-4 text-sm font-semibold text-gray-600">Status</th>
                    <th className="p-4 text-sm font-semibold text-gray-600">Rating</th>
                    <th className="p-4 text-sm font-semibold text-gray-600">Last Tested</th>
                    <th className="p-4 text-sm font-semibold text-gray-600">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.length > 0 ? filteredProducts.map((p) => (
                    <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="p-4">
                        <div className="font-bold text-gray-900">{p.brand}</div>
                        <div className="text-sm text-gray-500">{p.product}</div>
                      </td>
                      <td className="p-4">
                        {getStatusBadge(p.status)}
                      </td>
                      <td className="p-4">
                        <span className={`font-bold text-lg ${
                          p.safetyRating === 'A' ? 'text-green-600' : 
                          p.safetyRating === 'F' ? 'text-red-600' : 'text-yellow-600'
                        }`}>
                          {p.safetyRating}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-gray-600">{p.lastTested}</td>
                      <td className="p-4 text-sm text-gray-600 max-w-xs">{p.notes}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="5" className="p-8 text-center text-gray-500">
                        No products found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductSearch;